import { pick } from 'lodash';
import constants from '~/utils/constants';
import {
    isAssignmentAtRisk,
    isAssignmentEarly,
    isAssignmentLate,
    isAssignmentOnTime
} from '~/utils/assignment-utils';
import { idUtils } from '~/utils/id-utils';
import { parseCompartmentDetails } from '~/utils/compartment-utils';
import { BaseStop } from '~/data-classes/abstract/BaseStop';
import type { ApiStop, StopCompartmentDetail } from '~/api/types';
import type { CSSProperties } from 'react';
import type { LiveStop } from '../dispatched';

/**
 * PlanStop data class
 *
 * @category Data Classes
 *
 * @example
 * import { PlanStop } from '~/data-classes';
 *
 * const srcData = {};
 * const planStop = new PlanStop(srcData);
 *
 */
class PlanStop extends BaseStop {
    /**
     * The API source data
     */
    private readonly _stopLevelData: ApiStop;

    private _colorCSS: Partial<CSSProperties>;

    private _pairedStop: PlanStop | LiveStop | undefined;

    constructor(stopLevelData: ApiStop) {
        super();
        this._stopLevelData = stopLevelData;
        this._colorCSS = {
            color: 'white',
            backgroundColor: 'gray'
        };
        this._pairedStop = undefined;
    }

    /**
     * the plan stop client ID
     */
    get clientId() {
        return this._stopLevelData.clientId;
    }

    /**
     * the plan stop route ID
     */
    get routeId() {
        return this._stopLevelData.routeId;
    }

    /**
     * the plan stop client-route ID
     */
    get clientRouteId() {
        return idUtils.getCombinedId(this.clientId, this.routeId);
    }

    /**
     * the plan stop task ID, an alias of [taskId]{@link PlanStop#taskId}
     * For regular tasks stopId equals taskId
     * For two part task stopId will be `pickup_taskId` and `delivery_taskId`
     */
    get stopId() {
        if (this.isTwoPart && !this.isDepot) {
            return PlanStop.normalizeTwoPartStopId(
                this._stopLevelData.stopId,
                this.type
            );
        }
        return this._stopLevelData.stopId;
    }

    get apiStopId() {
        return this._stopLevelData.stopId;
    }

    /**
     * The route plan stores stop IDs in different formats
     * - Taxicab: pickup_pickup_<uuid>DELIVERYdelivery_<uuid>
     */
    get breakApiStopId(): string {
        const id = this.apiStopId;
        const shouldStripPrefix =
            id.includes('DELIVERY') || (this.isTwoPart && !this.euid);

        if (!shouldStripPrefix) return id;
        if (id.startsWith('pickup_')) return id.slice('pickup_'.length);
        if (id.startsWith('delivery_')) return id.slice('delivery_'.length);

        return id;
    }

    /**
     * the plan stop task ID
     */
    get taskId() {
        if (this.isTwoPart && !this.isDepot) {
            return PlanStop.normalizeTwoPartStopId(
                this._stopLevelData.task.taskId,
                this.type
            );
        }
        return this._stopLevelData.task.taskId;
    }

    /**
     * the plan stop task ID, an alias of [taskId]{@link PlanStop#taskId}
     */
    get id() {
        return this.taskId;
    }

    /**
     * the plan stop client-route-task ID
     */
    get clientRouteTaskId() {
        return idUtils.getCombinedId(this.clientRouteId, this.taskId);
    }

    /**
     * the plan stop extended task object
     */
    get task() {
        return {
            ...this._stopLevelData.task,
            id: this._stopLevelData.task.taskId,
            stopName: this._stopLevelData.task.location.name,
            arrivalTime: this._stopLevelData.task.eta,
            clientRouteTaskId: this.clientRouteTaskId
        };
    }

    /**
     * the plan stop address object
     */
    get address() {
        return pick(this._stopLevelData.task.location, [
            'addressLine1',
            'addressLine2',
            'city',
            'state',
            'zipcode',
            'id'
        ]);
    }

    /**
     * the plan stop full address object
     */
    get location() {
        return this._stopLevelData.task.location;
    }

    /* @alias taskName */
    /**
     * the plan task stop name
     */
    get stopName() {
        return this._stopLevelData.task.location.name;
    }

    /**
     * the estimated time of arrival
     */
    get eta() {
        return this._stopLevelData.task.eta;
    }

    /**
     * the estimated time of arrival, expressed in milliseconds
     */
    get rawEta() {
        return this._stopLevelData.task.rawEta;
    }

    /**
     * route id provided by client
     */
    get euid() {
        return this._stopLevelData.task.euid;
    }

    /**
     * the plan stop estimated arrival time, an alias of [eta]{@link PlanStop#eta}
     */
    get arrivalTime() {
        return this.eta;
    }

    /**
     * the plan stop estimated service time expressed as ISO duration string
     */
    get serviceTime() {
        return this._stopLevelData.task.serviceTime;
    }

    /**
     * whether the plan stop service time is estimated by machine learning service times (MLST)
     */
    get isServiceTimeML() {
        return this._stopLevelData.task.serviceTimeML;
    }

    /**
     * the plan stop service time window
     */
    get timeWindow() {
        return this._stopLevelData.task.window;
    }

    /**
     * the plan stop service time window, an alias of [timeWindow]{@link PlanStop#timeWindow}
     */
    get rawTimeWindow() {
        return this.timeWindow;
    }

    /**
     * the plan stop route date as ISO date
     */
    get routeDate() {
        return this._stopLevelData.task.routeDate;
    }

    /**
     * the plan stop route delay code
     */
    get delayFlag() {
        return (
            this._stopLevelData.task.multiTimeWindowDelay ??
            this._stopLevelData.task.delayFlag
        );
    }

    /**
     * the plan stop route delay code
     */
    get delay() {
        return this.delayFlag;
    }

    /**
     * whether the plan stop arrival is early
     */
    get isEarly() {
        return isAssignmentEarly(this.delayFlag);
    }

    /**
     * whether the plan stop arrival is on-time
     */
    get isOnTime() {
        return isAssignmentOnTime(this.delayFlag);
    }

    /**
     * whether the plan stop arrival is at-risk
     */
    get isAtRisk() {
        return isAssignmentAtRisk(this.delayFlag);
    }

    /**
     * whether the plan stop arrival is late
     */
    get isLate() {
        return isAssignmentLate(this.delayFlag);
    }

    /**
     * whether the plan stop sequence number. Note, for a route with multiple reloads,
     * this is not indicative of the overall sequence.
     */
    get stopNumber() {
        return this._stopLevelData.task.stopNumber;
    }

    /**
     * Returns the plan stop sequence number.
     * It is indicative of the overall sequence in case of multi trip route (route with reloads)
     */
    get driverStopNumber() {
        return this._stopLevelData.task.driverStopNumber;
    }

    /**
     * the plan stop labels
     */
    get labels() {
        return this._stopLevelData.task.labels || [];
    }

    /**
     * whether the plan stop is planned
     */
    get isPlanned() {
        return this._stopLevelData.task.isPlanned;
    }

    /**
     * whether the plan stop is joined with another task (two-part task)
     */
    get isTwoPart() {
        return this._stopLevelData.task.isTwoPart;
    }

    /**
     * Sets paired stop for two-part tasks
     * Applicable for carpooling when pickup and deliveries might not be in order
     * @param pairedStop instance of PlanStop
     */
    set pairedStop(pairedStop) {
        this._pairedStop = pairedStop;
    }

    get pairedStop() {
        return this._pairedStop;
    }

    /**
     * the plan stop valid two-part task ID without task type
     * only taxi-cab tasks will have task type prefix in taskId
     * carpooling two-part tasks will not have any task type prefix
     */
    get twoPartTaskId() {
        if (!this.isTwoPart) return null;
        const idParts = idUtils.splitCombinedId(this.taskId);

        if (idParts.length > 1) return idParts[1];

        return this.taskId;
    }

    /**
     * whether the plan stop is a depot
     */
    get isDepot() {
        return this._stopLevelData.task.type === constants.taskTypes.DEPOT;
    }

    /**
     * whether the plan stop is a start depot
     */
    get isStartDepot(): boolean {
        return this.isDepot && this.stopId === 'depot1';
    }

    /**
     * whether the plan stop is an end depot
     */
    get isEndDepot(): boolean {
        return this.isDepot && this.stopId === 'depot2';
    }

    /**
     * the plan stop task type
     */
    get type() {
        return this._stopLevelData.task.type;
    }

    /**
     * whether the plan stop is a pickup task
     */
    get isPickup() {
        return this.type === constants.taskTypes.PICKUP;
    }

    /**
     * whether the plan stop is a delivery task
     */
    get isDelivery() {
        return this.type === constants.taskTypes.DELIVERY;
    }

    /**
     * whether the plan stop is a high-priority task
     */
    get isHighPriority() {
        return (
            this._stopLevelData.task.priority === constants.priorityCodes.HIGH
        );
    }

    /**
     * the plan stop marker coordinates
     */
    get markerCoordinates() {
        return this._stopLevelData.task.markerCoordinates;
    }

    /**
     * the plan stop vehicle volume capacity used
     */
    get volumeCapacityUsed() {
        return this._stopLevelData.task.stats.volumeCapacityUsed;
    }

    /**
     * the plan stop size, an alias of [volumeCapacityUsed]{@link PlanStop#volumeCapacityUsed}
     */
    get size() {
        return this._stopLevelData.task.stats.volumeCapacityUsed;
    }

    /**
     * The plan stop size by compartment
     * For multi-compartment account setup - array of 1+
     * For single-compartment account setup - array of 1
     */
    get sizeByCompartment() {
        const sizeByCompartment =
            this._stopLevelData.task?.stats?.sizeByCompartment;
        return (sizeByCompartment || []).map(
            (compartment: StopCompartmentDetail) => {
                return parseCompartmentDetails(compartment);
            }
        );
    }

    /**
     * the plan stop vehicle weight capacity used
     */
    get weightUsed() {
        return this._stopLevelData.task.stats.weightUsed;
    }

    /**
     * the plan stop number of inventory items
     */
    get numInventoryItems() {
        return this._stopLevelData.task.stats.numInventoryItems;
    }

    /**
     * the plan stop assignment recommendation score from machine learning data analysis (MLDA)
     *
     * a recommendation score greater than zero is considered `ML Assigned`
     */
    get recommendationScore() {
        return this._stopLevelData.recommendationScore;
    }

    /**
     * the plan stop height
     */
    get height() {
        return this._stopLevelData.task.stats.height;
    }

    /**
     * the plan stop assigned web color
     */
    get colorCSS() {
        return this._colorCSS;
    }

    /**
     * update the plan stop assigned web color
     */
    set colorCSS(colorCSS: Partial<CSSProperties>) {
        this._colorCSS = colorCSS;
    }

    /**
     * the plan stop driver break duration in ms
     */
    get breakDuration() {
        return this._stopLevelData.task.breakDetails?.duration || 0;
    }

    /**
     * the plan stop driver break details
     */
    get breakDetails() {
        return this._stopLevelData.task.breakDetails;
    }

    get tripNumber() {
        return this._stopLevelData.tripNumber;
    }

    /**
     * verifies that this PlanStop is the same as another
     */
    checkIsSame(planStop: PlanStop) {
        const isAPlanned = this.isPlanned;
        const isBPlanned = planStop.isPlanned;
        const taskIdA = this.taskId;
        const taskIdB = planStop.taskId;
        const hasMatchingIds = taskIdA === taskIdB;
        if (isAPlanned && isBPlanned) {
            return hasMatchingIds;
        }
        if (!isAPlanned && !isBPlanned) {
            const isATwoPart = this.isTwoPart;
            const isBTwoPart = planStop.isTwoPart;
            if (isATwoPart && isBTwoPart) {
                const aType = this.type;
                const bType = planStop.type;
                const hasSameType = aType === bType;
                return hasSameType && hasMatchingIds;
            }
            return hasMatchingIds;
        }
        return false;
    }

    /**
     * Serializes this class back to JSON
     */
    toJSON() {
        const { task } = this;

        return {
            ...this._stopLevelData,
            task
        };
    }
}

export default PlanStop;
